import { useState, useEffect } from 'react'
import { FiChevronDown, FiChevronUp, FiMail, FiSend, FiHelpCircle, FiUser, FiCalendar, FiSettings, FiTag } from 'react-icons/fi'
import toast from 'react-hot-toast'
import { getFaqs, submitContact } from '../services/api'

// Icon mapping for FAQ categories
const iconMap = {
    ticket: FiTag,
    user: FiUser,
    calendar: FiCalendar,
    settings: FiSettings,
}

export default function HelpCenterPage() {
    const [faqs, setFaqs] = useState([])
    const [loading, setLoading] = useState(true)
    const [expandedFaq, setExpandedFaq] = useState(null)
    const [contactForm, setContactForm] = useState({
        name: '',
        email: '',
        subject: '',
        message: ''
    })
    const [sending, setSending] = useState(false)

    const currentLanguage = localStorage.getItem('app_lang')

    useEffect(() => {
        loadFaqs()
    }, [])

    const loadFaqs = async () => {
        try {
            const res = await getFaqs()
            setFaqs(res.data.data || [])
        } catch (error) {
            toast.error(window.t('greshka_pri_zarezhdane_na_dannite'))
        } finally {
            setLoading(false)
        }
    }

    const handleContactSubmit = async (e) => {
        e.preventDefault()
        setSending(true)
        try {
            await submitContact(contactForm)
            toast.success(window.t('sabshchenieto_e_izprateno_uspeshno_shte_se_svarzhem_s_vas_skoro'))
            setContactForm({ name: '', email: '', subject: '', message: '' })
        } catch (error) {
            toast.error(error.response?.data?.message || window.t('greshka_pri_izprashtane_na_sabshchenieto'))
        } finally {
            setSending(false)
        }
    }

    const toggleFaq = (faqId) => {
        setExpandedFaq(expandedFaq === faqId ? null : faqId)
    }

    return (
        <div className="min-h-screen py-8 mt-16">
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="text-center mb-12">
                    <div className="flex items-center justify-center mb-4">
                        <div className="w-16 h-16 bg-gradient-to-br from-primary-500 to-accent-500 rounded-2xl flex items-center justify-center">
                            <FiHelpCircle className="w-8 h-8 text-white" />
                        </div>
                    </div>
                    <h1 className="text-4xl font-bold text-white mb-4">{window.t('pomoshten_tsentar')}</h1>
                    <p className="text-gray-400 max-w-2xl mx-auto">
                        {window.t('namerete_otgovori_na_chesto_za')}
                    </p>
                </div>

                <div className="grid lg:grid-cols-3 gap-8">
                    {/* FAQs Section */}
                    <div className="lg:col-span-2 space-y-6">
                        <h2 className="text-2xl font-bold text-white mb-6">{window.t('chesto_zadavani_vaprosi')}</h2>

                        {loading ? (
                            <div className="flex items-center justify-center py-12">
                                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary-500"></div>
                            </div>
                        ) : faqs.length === 0 ? (
                            <div className="card p-8 text-center">
                                <FiHelpCircle className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                                <p className="text-gray-400">{window.t('v_momenta_nyama_nalichni_chest')}</p>
                            </div>
                        ) : (
                            faqs.map((category) => {
                                const IconComponent = iconMap[category.icon] || FiHelpCircle
                                return (
                                    <div key={category.id} className="card overflow-hidden">
                                        {/* Category Header */}
                                        <div className="bg-gray-800/50 px-6 py-4 border-b border-gray-700">
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 bg-primary-500/20 rounded-xl flex items-center justify-center">
                                                    <IconComponent className="w-5 h-5 text-primary-400" />
                                                </div>
                                                <div>
                                                    <h3 className="text-lg font-semibold text-white">{currentLanguage === 'en' ? (category.name_en || category.name) : category.name}</h3>
                                                    {category.description && (
                                                        <p className="text-sm text-gray-400">{currentLanguage === 'en' ? (category.description_en || category.description) : category.description}</p>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        {/* FAQ Items */}
                                        <div className="divide-y divide-gray-800">
                                            {category.faqs?.map((faq) => (
                                                <div key={faq.id}>
                                                    <button
                                                        onClick={() => toggleFaq(faq.id)}
                                                        className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-800/30 transition-colors"
                                                    >
                                                        <span className="text-white font-medium pr-4">{currentLanguage === 'en' ? (faq.question_en || faq.question) : faq.question}</span>
                                                        {expandedFaq === faq.id ? (
                                                            <FiChevronUp className="w-5 h-5 text-primary-400 flex-shrink-0" />
                                                        ) : (
                                                            <FiChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
                                                        )}
                                                    </button>
                                                    {expandedFaq === faq.id && (
                                                        <div className="px-6 pb-4 text-gray-300 leading-relaxed animate-fadeIn">
                                                            {currentLanguage === 'en' ? (faq.answer_en || faq.answer) : faq.answer}
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )
                            })
                        )}
                    </div>

                    {/* Contact Form Section */}
                    <div className="lg:col-span-1">
                        <div className="card p-6 sticky top-8">
                            <div className="flex items-center gap-3 mb-6">
                                <div className="w-10 h-10 bg-accent-500/20 rounded-xl flex items-center justify-center">
                                    <FiMail className="w-5 h-5 text-accent-400" />
                                </div>
                                <div>
                                    <h3 className="text-lg font-semibold text-white">{window.t('svarzhete_se_s_nas')}</h3>
                                    <p className="text-sm text-gray-400">{window.t('svarzhete_se_s_nashiya_ekip')}</p>
                                </div>
                            </div>

                            <form onSubmit={handleContactSubmit} className="space-y-4">
                                <div>
                                    <label className="label">{window.t('ime')}</label>
                                    <input
                                        type="text"
                                        value={contactForm.name}
                                        onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                                        className="input"
                                        placeholder={window.t('vasheto_ime')}
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="label">{window.t('imeyl')}</label>
                                    <input
                                        type="email"
                                        value={contactForm.email}
                                        onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                                        className="input"
                                        placeholder="your@email.com"
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="label">{window.t('tema')}</label>
                                    <input
                                        type="text"
                                        value={contactForm.subject}
                                        onChange={(e) => setContactForm({ ...contactForm, subject: e.target.value })}
                                        className="input"
                                        placeholder={window.t('kak_mozhem_da_pomognem')}
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="label">{window.t('saobshtenie')}</label>
                                    <textarea
                                        value={contactForm.message}
                                        onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                                        className="input min-h-[120px] resize-none"
                                        placeholder={window.t('opishete_vashiya_problem_ili_v')}
                                        required
                                    />
                                </div>

                                <button
                                    type="submit"
                                    disabled={sending}
                                    className="btn-primary w-full flex items-center justify-center gap-2"
                                >
                                    {sending ? (
                                        <>
                                            <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
                                            {window.t('izprashtane')}
                                        </>
                                    ) : (
                                        <>
                                            <FiSend className="w-4 h-4" />
                                            {window.t('izprati_saobshtenie')}
                                        </>
                                    )}
                                </button>
                            </form>

                            {/* Contact Info */}
                            <div className="mt-6 pt-6 border-t border-gray-700">
                                <p className="text-gray-400 text-sm">
                                    {window.t('mozhete_sashto_da_se_svarzhete')}
                                </p>
                                <p className="text-primary-400 text-sm mt-2">
                                    delo@blagoevgrad.bg
                                </p>
                                <p className="text-gray-400 text-sm mt-1">
                                    073 867701
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
